"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from "lucide-react";

export type ChristmasViewerImage = {
  publicUrl: string;
  alt: string;
};

type Props = {
  images: ChristmasViewerImage[];
  open: boolean;
  initialIndex?: number;
  onClose: () => void;
  labels?: {
    close?: string;
    previous?: string;
    next?: string;
    zoomIn?: string;
    zoomOut?: string;
    broken?: string;
    loading?: string;
    of?: string;
  };
};

/**
 * Lightweight original-image gallery for Christmas packages.
 * Mobile: swipe; desktop: prev/next; zoom + fullscreen where supported.
 * Never receives or displays storage paths.
 */
export function ChristmasOfferingImageViewer({
  images,
  open,
  initialIndex = 0,
  onClose,
  labels,
}: Props) {
  const [index, setIndex] = useState(initialIndex);
  const [broken, setBroken] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState<Record<number, boolean>>({});
  const [scale, setScale] = useState(1);
  const touchStartX = useRef<number | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const count = images.length;
  const safeIndex = count === 0 ? 0 : ((index % count) + count) % count;
  const current = count > 0 ? images[safeIndex] : null;

  useEffect(() => {
    if (open) {
      setIndex(Math.max(0, Math.min(initialIndex, Math.max(0, count - 1))));
      setScale(1);
    }
  }, [open, initialIndex, count]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") setIndex((i) => i - 1);
      if (e.key === "ArrowRight") setIndex((i) => i + 1);
      if (e.key === "+" || e.key === "=") setScale((s) => Math.min(3, s + 0.25));
      if (e.key === "-") setScale((s) => Math.max(1, s - 0.25));
    };
    window.addEventListener("keydown", onKey);
    dialogRef.current?.focus();
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const goPrev = useCallback(() => {
    setIndex((i) => i - 1);
    setScale(1);
  }, []);
  const goNext = useCallback(() => {
    setIndex((i) => i + 1);
    setScale(1);
  }, []);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touchStartX.current;
    touchStartX.current = null;
    if (start == null) return;
    const end = e.changedTouches[0]?.clientX ?? start;
    const delta = end - start;
    if (Math.abs(delta) < 48) return;
    if (delta > 0) goPrev();
    else goNext();
  };

  const requestFullscreen = async () => {
    const el = dialogRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else if (el.requestFullscreen) {
        await el.requestFullscreen();
      }
    } catch {
      // Fullscreen may be blocked; zoom still works.
    }
  };

  if (!open || !current) return null;

  const L = {
    close: labels?.close ?? "Cerrar",
    previous: labels?.previous ?? "Anterior",
    next: labels?.next ?? "Siguiente",
    zoomIn: labels?.zoomIn ?? "Ampliar",
    zoomOut: labels?.zoomOut ?? "Reducir",
    broken: labels?.broken ?? "No se pudo cargar la imagen",
    loading: labels?.loading ?? "Cargando…",
    of: labels?.of ?? "de",
  };

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={current.alt}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex flex-col bg-black/95 text-white outline-none"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2 sm:px-4">
        <p className="text-sm text-white/80" aria-live="polite">
          {safeIndex + 1} {L.of} {count}
        </p>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="rounded-lg p-2 hover:bg-white/10"
            aria-label={L.zoomOut}
            onClick={() => setScale((s) => Math.max(1, s - 0.25))}
          >
            <ZoomOut className="h-5 w-5" />
          </button>
          <button
            type="button"
            className="rounded-lg p-2 hover:bg-white/10"
            aria-label={L.zoomIn}
            onClick={() => {
              setScale((s) => Math.min(3, s + 0.25));
              void requestFullscreen();
            }}
          >
            <ZoomIn className="h-5 w-5" />
          </button>
          <button
            type="button"
            className="rounded-lg p-2 hover:bg-white/10"
            aria-label={L.close}
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden px-2 pb-4">
        {count > 1 && (
          <button
            type="button"
            className="absolute left-2 z-10 hidden rounded-full bg-white/10 p-2 hover:bg-white/20 sm:inline-flex"
            aria-label={L.previous}
            onClick={goPrev}
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}

        <div
          className="relative h-full w-full max-w-4xl"
          style={{ transform: `scale(${scale})`, transition: "transform 120ms ease" }}
        >
          {broken[safeIndex] ? (
            <div className="flex h-full min-h-[240px] items-center justify-center text-sm text-white/70">
              {L.broken}
            </div>
          ) : (
            <>
              {loading[safeIndex] !== false && (
                <div className="absolute inset-0 flex items-center justify-center text-sm text-white/60">
                  {L.loading}
                </div>
              )}
              <Image
                src={current.publicUrl}
                alt={current.alt}
                fill
                unoptimized
                className="object-contain"
                sizes="100vw"
                onLoadStart={() =>
                  setLoading((m) => ({ ...m, [safeIndex]: true }))
                }
                onLoadingComplete={() =>
                  setLoading((m) => ({ ...m, [safeIndex]: false }))
                }
                onError={() => {
                  setBroken((m) => ({ ...m, [safeIndex]: true }));
                  setLoading((m) => ({ ...m, [safeIndex]: false }));
                }}
                priority
              />
            </>
          )}
        </div>

        {count > 1 && (
          <button
            type="button"
            className="absolute right-2 z-10 hidden rounded-full bg-white/10 p-2 hover:bg-white/20 sm:inline-flex"
            aria-label={L.next}
            onClick={goNext}
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        )}
      </div>

      {count > 1 && (
        <div className="flex justify-center gap-2 pb-4 sm:hidden" aria-hidden>
          {images.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 w-1.5 rounded-full ${
                i === safeIndex ? "bg-white" : "bg-white/40"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
